import type { Locale } from '../i18n';

/**
 * Format an amount as a locale-aware currency string. Uses Intl.NumberFormat
 * so the symbol position, decimal separator, and thousand separator match
 * the rendering locale (€ 1.250,00 in DE vs €1,250.00 in EN).
 *
 * Defensive against bad currency codes — falls back to the numeric value
 * if Intl rejects the input.
 */
export function formatCurrency(amount: number, currency: string, locale: Locale): string {
    try {
        return new Intl.NumberFormat(localeToBcp47(locale), {
            style: 'currency',
            currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount);
    } catch {
        return amount.toFixed(2);
    }
}

export interface ValuePriceFormatOptions {
    displayPrice: boolean;
    displayPriceIfZero: boolean;
    /**
     * Wrapping pattern using `$price` as the placeholder
     * (e.g. `" (+$price)"` or `" [$price]"`). Falsy = no wrapper.
     */
    displayPricePattern: string | undefined;
    /** `relative` shows the delta against `relativeBasePrice`. */
    displayPriceType: 'absolute' | 'relative';
    /**
     * Base to subtract from the displayed amount when `displayPriceType` is
     * `relative` — typically the price of the currently picked Value within
     * the same Characteristic. Ignored in `absolute` mode.
     */
    relativeBasePrice?: number | string | undefined;
    currency: string;
    locale: Locale;
    /**
     * Rebases an authored amount onto the host's tax basis and currency —
     * the same conversion the total gets. Applied to the price AND the
     * relative base, so the suffix shows what the line will add to the total.
     */
    convert?: ((amount: number) => number) | undefined;
}

/**
 * Format the price *suffix* shown next to a Value (radio option, select
 * option, list-image caption, …). Returns an empty string when the suffix
 * should be hidden (displayPrice off, zero-suppress, unparseable input).
 *
 * Mirrors the Legacy `Justselling_Configento_App_Price.renderPrice` semantics:
 * - `displayPrice = false` → always empty
 * - amount is 0/NaN and `displayPriceIfZero = false` → empty
 * - positive amounts get a leading `+`
 * - `displayPricePattern` wraps the result by replacing `$price`
 */
export function formatValuePrice(
    rawPrice: string | number | undefined | null,
    opts: ValuePriceFormatOptions,
): string {
    if (!opts.displayPrice) return '';
    const parsed = parsePriceInput(rawPrice);
    if (parsed === null) return '';

    // Relative mode: subtract the base price so the suffix reflects the
    // *additional* cost vs the current pick (-5,00 / +0,00 / +3,00 …).
    const convert = opts.convert ?? ((n: number) => n);
    let amount = convert(parsed);
    if (opts.displayPriceType === 'relative') {
        const base = parsePriceInput(opts.relativeBasePrice);
        if (base !== null) amount = convert(parsed) - convert(base);
    }

    if (amount === 0 && !opts.displayPriceIfZero) return '';

    let formatted = formatCurrency(amount, opts.currency, opts.locale);
    // Intl.NumberFormat already prefixes "-" for negatives; only add "+" for positives.
    if (amount > 0) formatted = `+${formatted}`;

    if (opts.displayPricePattern && opts.displayPricePattern.length > 0) {
        return opts.displayPricePattern.replace('$price', formatted);
    }
    return formatted;
}

/**
 * Tolerant numeric coercion for legacy price fields: numbers pass through,
 * `"19.50"` and `"19,50"` parse; everything else → null.
 */
function parsePriceInput(raw: unknown): number | null {
    if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
    if (typeof raw !== 'string' || raw.length === 0) return null;
    const normalised = raw.replace(',', '.');
    const n = Number(normalised);
    return Number.isFinite(n) ? n : null;
}

function localeToBcp47(locale: Locale): string {
    // Our internal locale codes use underscores (`de_DE`); Intl wants hyphens.
    return locale.replace('_', '-');
}
